import { memo, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { FaArrowLeft } from "react-icons/fa";
import PropTypes from "prop-types";
import CTASection from "../components/CTASection";
import {
  GlobalContainer,
  H1,
  Paragraph,
  LegalWrapper,
  Logo,
  CTABanner,
  CTABannerButton,
} from "../styles/AppStyles";
import { Helmet } from "react-helmet-async";
import { imageSize } from "../utils/imageSize";
import { LINKS, href } from "../config/links";
import { getLogo } from "../utils/helpers";
import { getArticleSchema, toIsoDate } from "../seo/schema";

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.iconColor};
  font-size: 0.95rem;
  cursor: pointer;
  padding: 8px 0;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

const shouldHideCTABanner = (pathname) =>
  pathname.startsWith("/account") ||
  pathname.startsWith("/app/playbook");

/**
 * ContentLayout
 * Shared layout for Learning Center content pages.
 *
 * SEO priorities:
 * - canonical URL
 * - strong meta description
 * - Open Graph / Twitter cards
 * - optional JSON-LD
 * - LCP optimization: preload + eager hero image when present
 *
 * UX priorities:
 * - optional hero image with title superimposed
 * - consistent header/logo behavior (touch vs desktop)
 */
const ContentLayout = ({
  istouch,
  isdarkmode,

  // Title overlay styling
  // - tone: 'light' (white text) or 'dark' (black text)
  // - outline: adds contrasting outline for legibility
  titleTone = "light",
  titleOutline = true,
  showCTA = true,
  hideBackButton = false,
  // Display + SEO
  displayTitle,
  seoTitle,
  canonicalUrl,
  description,
  ogTitle,
  ogDescription,
  ogImage,
  ogImageAlt,
  ogType = "article",

  // Optional hero
  heroImageSrc,
  heroImageAlt,
  // Intrinsic size of the hero image; defaults to the generated image map
  // (src/config/imageDimensions.json). Override only for unusual cases.
  heroImageWidth,
  heroImageHeight,

  // Optional footer labels
  publishedLabel,
  lastUpdatedLabel,

  // Optional structured data (object -> JSON-LD)
  jsonLd,

  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const logoTop = useMemo(() => getLogo(isdarkmode), [isdarkmode]);
  const hideCTABanner = shouldHideCTABanner(location.pathname);

  const resolvedSeoTitle = seoTitle || ogTitle || displayTitle;

  // Quick guides are dated articles: give them Article structured data built
  // from what the page shows (title, description, visible dates). Other
  // ContentLayout pages only get JSON-LD they pass in.
  const resolvedJsonLd = useMemo(() => {
    if (jsonLd) return jsonLd;
    if (!location.pathname.startsWith(LINKS.QUICK_GUIDES.INDEX + "/") || !publishedLabel) return null;
    const image = ogImage || (heroImageSrc?.startsWith("/") ? `https://csfsafe.com${heroImageSrc}` : heroImageSrc);
    return getArticleSchema({
      headline: displayTitle,
      description,
      url: canonicalUrl || `https://csfsafe.com${location.pathname}`,
      image,
      datePublished: toIsoDate(publishedLabel),
      dateModified: toIsoDate(lastUpdatedLabel),
    });
  }, [jsonLd, location.pathname, publishedLabel, lastUpdatedLabel, ogImage, heroImageSrc, displayTitle, description, canonicalUrl]);
  const resolvedOgTitle = ogTitle || resolvedSeoTitle;
  const resolvedOgDescription = ogDescription || description;

  const twitterCard = ogImage ? "summary_large_image" : "summary";

  const resolvedTitleTextColor = titleTone === "dark" ? "#000" : "#fff";
  const resolvedTitleOutlineColor = titleTone === "dark" ? "#fff" : "#000";

  // On touch/phone, WebkitTextStroke can create interior artifacts ("crunching")
  // due to font hinting + rasterization at small sizes. Prefer an outline simulated
  // with text-shadow instead.
  const titleOutlineShadow = useMemo(() => {
    if (!titleOutline) return "0 3px 10px rgba(0,0,0,0.55)";
    const c = resolvedTitleOutlineColor;
    // 8-way crisp outline + a soft drop shadow for separation from busy imagery
    return [
      "-1px -1px 0 " + c,
      "0 -1px 0 " + c,
      "1px -1px 0 " + c,
      "-1px 0 0 " + c,
      "1px 0 0 " + c,
      "-1px 1px 0 " + c,
      "0 1px 0 " + c,
      "1px 1px 0 " + c,
      "0 3px 10px rgba(0,0,0,0.75)",
    ].join(", ");
  }, [titleOutline, resolvedTitleOutlineColor]);

  return (
    <GlobalContainer $istouch={istouch} style={{ marginBottom: ".8rem" }}>
      {!hideBackButton && (
        <BackButton onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </BackButton>
      )}

      {/* ---------------------- SEO + Preload ---------------------- */}
      <Helmet>
        <title>{resolvedSeoTitle}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index,follow" />
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

        {/* Open Graph */}
        <meta property="og:type" content={ogType} />
        <meta property="og:title" content={resolvedOgTitle} />
        <meta property="og:description" content={resolvedOgDescription} />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:site_name" content="CSFSafe" />
        {ogImage && <meta property="og:image" content={ogImage} />}
        {ogImageAlt && <meta property="og:image:alt" content={ogImageAlt} />}

        {/* Twitter */}
        <meta name="twitter:card" content={twitterCard} />
        <meta name="twitter:title" content={resolvedOgTitle} />
        <meta name="twitter:description" content={resolvedOgDescription} />
        {ogImage && <meta name="twitter:image" content={ogImage} />}

        {/* Preload LCP hero image for better LCP */}
        {heroImageSrc && (
          <link
            rel="preload"
            as="image"
            href={heroImageSrc}
            fetchPriority="high"
          />
        )}

        {resolvedJsonLd && (
          <script type="application/ld+json">
            {JSON.stringify(resolvedJsonLd)}
          </script>
        )}
      </Helmet>

      {/* ---------------------- Page Header ---------------------- */}
      {istouch ? (
        <Logo style={{ position: "relative", zIndex: 0, margin: "0" }}>
          <img
            src={logoTop}
            alt="CSFSafe Logo"
            width={180}
            height={60}
            decoding="async"
            style={{
              maxHeight: "60px",
              minHeight: "40px",
              objectFit: "contain",
              margin: "1rem",
              display: "block",
            }}
          />
        </Logo>
      ) : (
        <H1 as="div" data-print="hide">
          Cybersecurity Learning Center
        </H1>
      )}

      {/* ---------------------- Content ---------------------- */}
      <LegalWrapper>
        <p className="print-only print-source">
          CSFSafe — {canonicalUrl || `https://csfsafe.com${location.pathname}`}
        </p>
        {heroImageSrc ? (
          <div
            style={{
              position: "relative",
              marginTop: "1.5rem",
              marginBottom: "1rem",
              width: "100%",
              textAlign: "center",
            }}
          >
            <img
              src={heroImageSrc}
              alt={heroImageAlt || displayTitle}
              width={heroImageWidth ?? imageSize(heroImageSrc)[0]}
              height={heroImageHeight ?? imageSize(heroImageSrc)[1]}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              sizes="(min-width: 800px) 762px, 100vw"
              style={{
                width: "100%",
                maxWidth: "762px",
                height: "auto",
                borderRadius: "12px",
                objectFit: "cover",
                display: "block",
                marginInline: "auto",
              }}
            />

            {/* Dark gradient improves legibility over varied imagery */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                left: "50%",
                transform: "translateX(-50%)",
                width: "100%",
                maxWidth: "762px",
                borderRadius: "12px",
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.10) 45%, rgba(0,0,0,0.65) 100%)",
              }}
            />

            <H1
              style={{
                position: "absolute",
                left: "50%",
                bottom: "18px",
                transform: "translateX(-50%)",
                margin: 0,
                padding: "0.75rem 1rem",
                width: "min(1100px, calc(100% - 2rem))",
                textAlign: "center",
                fontSize: "clamp(2.8rem, 4.6vw, 4.1rem)",
                fontFamily: "Arial, Helvetica, sans-serif",
                lineHeight: 1.12,
                fontWeight: 900,
                color: resolvedTitleTextColor,
                WebkitTextStroke:
                  !istouch && titleOutline
                    ? `2px ${resolvedTitleOutlineColor}`
                    : "0px transparent",
                textShadow: istouch
                  ? titleOutlineShadow
                  : titleOutline
                  ? "0 3px 10px rgba(0,0,0,0.75), 0 0 2px rgba(0,0,0,0.9)"
                  : "0 3px 10px rgba(0,0,0,0.75)",
              }}
            >
              {displayTitle}
            </H1>
          </div>
        ) : (
          <H1>{displayTitle}</H1>
        )}

        {publishedLabel && (
          <Paragraph className="last-updated">{publishedLabel}</Paragraph>
        )}

        {/* Use article semantics for SEO/accessibility */}
        <article>{children}</article>

        {!hideCTABanner && (
          <CTABanner data-print="hide">
            <span>Ready to build your cybersecurity playbook?</span>
            <CTABannerButton
              href={href(LINKS.ACCOUNT.SUBSCRIBE)}
              aria-label="Get the CSFSafe Cybersecurity Playbook desktop app"
            >
              Get the App — $499 one-time purchase
            </CTABannerButton>
          </CTABanner>
        )}

        {showCTA && <CTASection />}

        {lastUpdatedLabel && (
          <Paragraph className="last-updated">{lastUpdatedLabel}</Paragraph>
        )}
      </LegalWrapper>
    </GlobalContainer>
  );
};

ContentLayout.propTypes = {
  istouch: PropTypes.bool.isRequired,
  isdarkmode: PropTypes.bool.isRequired,
  showCTA: PropTypes.bool,
  hideBackButton: PropTypes.bool,

  titleTone: PropTypes.oneOf(["light", "dark"]),
  titleOutline: PropTypes.bool,

  displayTitle: PropTypes.string.isRequired,
  seoTitle: PropTypes.string,
  canonicalUrl: PropTypes.string,
  description: PropTypes.string.isRequired,

  ogTitle: PropTypes.string,
  ogDescription: PropTypes.string,
  ogImage: PropTypes.string,
  ogImageAlt: PropTypes.string,
  ogType: PropTypes.string,

  heroImageSrc: PropTypes.string,
  heroImageAlt: PropTypes.string,
  heroImageWidth: PropTypes.number,
  heroImageHeight: PropTypes.number,

  publishedLabel: PropTypes.string,
  lastUpdatedLabel: PropTypes.string,

  jsonLd: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),

  children: PropTypes.node.isRequired,
};

export default memo(ContentLayout);
